// src/components/SettingsModal.tsx
'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { X, Check, Shield } from 'lucide-react'
import type {
  CookieConsentConfig,
  TextsConfig,
  CategoryDefinition,
  ConsentState,
  StyleConfig,
} from '../types'
import { getCategoryLabel, getCategoryLongDescription } from '../utils/translation-helpers'

export interface SettingsModalProps {
  config: CookieConsentConfig
  texts: TextsConfig
  categories: CategoryDefinition[]
  currentConsent: ConsentState
  onSave: (choices: ConsentState) => void
  onCancel: () => void
  accentColor: string
  style: Required<StyleConfig>
}

export function SettingsModal({
  config,
  texts,
  categories,
  currentConsent,
  onSave,
  onCancel,
  accentColor,
  style,
}: SettingsModalProps) {
  const { language = 'en' } = config
  const modalRef = useRef<HTMLDivElement>(null)
  const [choices, setChoices] = useState<ConsentState>(() => {
    const initial: ConsentState = {}
    categories.forEach(category => {
      initial[category.id] = currentConsent[category.id] || category.required || false
    })
    return initial
  })

  const handleToggle = (categoryId: string, required: boolean) => {
    if (required) return
    setChoices(prev => ({ ...prev, [categoryId]: !prev[categoryId] }))
  }

  const handleSave = () => onSave(choices)

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel()
    }
  }

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => document.removeEventListener('keydown', handleEscKey)
  }, [onCancel])

  const borderRadiusValue = style.borderRadius === 'sharp' ? '0' : '16px'
  const buttonBorderRadiusValue = style.borderRadius === 'sharp' ? '0' : '10px'

  return (
    <>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleOverlayClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 999998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Modal */}
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={e => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '90%',
            maxWidth: '560px',
            maxHeight: '85vh',
            backgroundColor: '#ffffff',
            borderRadius: borderRadiusValue,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
          }}
        >
          {/* Header */}
          <div style={{ padding: '24px', borderBottom: '1px solid #f3f4f6' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: `${accentColor}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Shield style={{ width: '20px', height: '20px', color: accentColor }} />
                </div>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>
                  {texts.settingsHeading}
                </h2>
              </div>
              <button
                type="button"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onCancel()
                }}
                style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  zIndex: 10,
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                aria-label={texts.closeButton}
              >
                <X style={{ width: '18px', height: '18px', color: '#9ca3af' }} />
              </button>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
              {texts.settingsDescription}
            </p>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {categories.map(category => {
                const isChecked = choices[category.id]
                return (
                  <div
                    key={category.id}
                    role="checkbox"
                    aria-checked={isChecked}
                    tabIndex={category.required ? -1 : 0}
                    onKeyDown={e => {
                      if ((e.key === 'Enter' || e.key === ' ') && !category.required) {
                        e.preventDefault()
                        handleToggle(category.id, category.required)
                      }
                    }}
                    style={{
                      padding: '16px',
                      backgroundColor: isChecked ? `${accentColor}06` : '#fafafa',
                      borderRadius: '12px',
                      border: isChecked ? `2px solid ${accentColor}30` : '2px solid #f0f0f0',
                      transition: 'all 0.2s',
                      cursor: category.required ? 'not-allowed' : 'pointer',
                      opacity: category.required ? 0.7 : 1,
                    }}
                    onClick={() =>
                      !category.required && handleToggle(category.id, category.required)
                    }
                  >
                    <div style={{ display: 'flex', gap: '14px' }}>
                      <div style={{ position: 'relative', marginTop: '3px' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={category.required}
                          onChange={() => {}}
                          style={{ position: 'absolute', opacity: 0 }}
                          aria-hidden="true"
                        />
                        <div
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '6px',
                            border: isChecked ? `2px solid ${accentColor}` : '2px solid #d1d5db',
                            backgroundColor: isChecked ? accentColor : '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                          }}
                        >
                          {isChecked && (
                            <Check
                              style={{
                                width: '14px',
                                height: '14px',
                                color: '#ffffff',
                                strokeWidth: 3,
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: 600,
                            color: '#111827',
                            marginBottom: '6px',
                          }}
                        >
                          {getCategoryLabel(category, language)}
                          {category.required && (
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: 400,
                                marginLeft: '8px',
                              }}
                            >
                              (vždy aktivní)
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
                          {getCategoryLongDescription(category, language)}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '20px 24px',
              borderTop: '1px solid #f3f4f6',
              display: 'flex',
              gap: '12px',
            }}
          >
            <button
              type="button"
              onClick={handleSave}
              style={{
                flex: 1,
                padding: '13px 20px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#ffffff',
                backgroundColor: accentColor,
                border: 'none',
                borderRadius: buttonBorderRadiusValue,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            >
              {texts.saveButton}
            </button>
            <button
              type="button"
              onClick={onCancel}
              style={{
                flex: 1,
                padding: '13px 20px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151',
                backgroundColor: 'transparent',
                border: '2px solid #e5e7eb',
                borderRadius: buttonBorderRadiusValue,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f9fafb'
                e.currentTarget.style.borderColor = '#d1d5db'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.borderColor = '#e5e7eb'
              }}
            >
              {texts.cancelButton}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </>
  )
}
